import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, ExternalLink, PenLine } from "lucide-react";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";
import { REVIEWS as FALLBACK, RATING as FALLBACK_RATING, GOOGLE_REVIEWS_URL } from "@/config";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= Math.round(n) ? "fill-brand text-brand" : "text-slate-300"}`} />
    ))}
  </div>
);

export const Reviews = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/google-reviews`)
      .then((res) => {
        if (mounted) setData(res.data);
      })
      .catch(() => {
        if (mounted)
          setData({
            rating: FALLBACK_RATING.avg,
            userRatingCount: FALLBACK_RATING.count,
            reviews: FALLBACK.map((r) => ({
              authorName: r.name, rating: r.rating, text: r.text, relativePublishTime: r.date, authorProfilePhotoUrl: null,
            })),
            writeReviewUrl: null,
            source: "fallback",
          });
      });
    return () => { mounted = false; };
  }, []);

  const rating = data?.rating ?? FALLBACK_RATING.avg;
  const count = data?.userRatingCount ?? FALLBACK_RATING.count;
  const reviews = data?.reviews?.length ? data.reviews : FALLBACK.map((r) => ({ authorName: r.name, rating: r.rating, text: r.text, relativePublishTime: r.date, authorProfilePhotoUrl: null }));
  const writeReviewUrl = data?.writeReviewUrl || GOOGLE_REVIEWS_URL;

  return (
    <section id="reviews" className="py-24 bg-[#f8fafc]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">{t.reviews.eyebrow}</p>
            <h2 className="font-head text-3xl sm:text-4xl font-semibold text-navy tracking-tight">{t.reviews.title}</h2>
            <div className="mt-5 flex items-center gap-4">
              <span className="font-head text-5xl font-bold text-navy" data-testid="rating-average">{Number(rating).toFixed(1)}</span>
              <div>
                <Stars n={rating} />
                <p className="text-sm text-slate-500 mt-1" data-testid="rating-count">{count} {t.reviews.ratingSuffix}</p>
              </div>
              <img src="https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9d75d4bac872d951acd0aca01/g.webp=s96-fcrop64=1,00000000ffffffff-rw" alt="Google" className="h-8 w-8 ml-2 hidden sm:block" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 self-start">
            <a
              data-testid="google-reviews-btn"
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-lift inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-semibold px-6 py-3.5 rounded-md"
            >
              {t.reviews.googleBtn} <ExternalLink className="w-4 h-4" />
            </a>
            <a
              data-testid="write-review-btn"
              href={writeReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-lift inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3.5 rounded-md shadow-md shadow-brand/20"
            >
              <PenLine className="w-4 h-4" /> {t.reviews.writeBtn}
            </a>
          </div>
        </div>

        <Carousel opts={{ align: "start", loop: true }} className="w-full" data-testid="reviews-carousel">
          <CarouselContent className="-ml-4">
            {reviews.map((r, i) => (
              <CarouselItem key={i} data-testid={`review-card-${i}`} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                  className="h-full bg-white border border-navy/10 rounded-md p-6 hover:border-brand/40 hover:shadow-lg transition-all flex flex-col"
                >
                  <Stars n={r.rating} />
                  <p className="mt-4 text-slate-700 text-sm leading-relaxed flex-1">“{r.text}”</p>
                  <div className="mt-5 flex items-center gap-3">
                    {r.authorProfilePhotoUrl ? (
                      <img src={r.authorProfilePhotoUrl} alt={r.authorName} className="w-9 h-9 rounded-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-navy text-white flex items-center justify-center text-sm font-semibold">
                        {(r.authorName || "?").charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-semibold text-navy">{r.authorName}</p>
                      <p className="text-xs text-slate-500">{r.relativePublishTime}</p>
                    </div>
                  </div>
                </motion.div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="hidden sm:block">
            <CarouselPrevious data-testid="reviews-prev" className="border-navy/20 text-navy hover:bg-navy hover:text-white" />
            <CarouselNext data-testid="reviews-next" className="border-navy/20 text-navy hover:bg-navy hover:text-white" />
          </div>
        </Carousel>
      </div>
    </section>
  );
};
